import { SelectResult } from '@mode-switch/query';
import { FlamingoTypes } from '../..';

/**
 * TODO: Rework this so only types that need to be used in the domain are included
 * and everything else is in the data-access module
 **/

export const enum ResponseType {
  // Ready
  ReadyCommitted = 'ReadyCommitted',

  // Helix
  HelixInProgress = 'HelixInProgress',

  // Select
  SelectSuccess = 'SelectSuccess',

  // Errors
  DatasetNotFound = 'DatasetNotFound',
  DatasetFailed = 'DatasetFailed',
  SignatureExpired = 'SignatureExpired',
  ServerError = 'ServerError',
  UnknownError = 'UnknownError',
  SizeLimit = 'SizeLimit',
  TimeoutError = 'TimeoutError',

  // Flamingo Error
  ServiceError = 'FlamingoError',
}

export const enum DatasetState {
  Failed = 'FAILED',
  Created = 'CREATED',
  Allocated = 'ALLOCATED',
  Committed = 'COMMITTED',
  Valid = 'VALID',
  Invalid = 'INVALID',
  ValidationInProgress = 'VALIDATION_IN_PROGRESS',
}

export const enum HelixResponseStatus {
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
  Executing = 'EXECUTING',
}

export type DatasetResult =
  | DatasetFailureResult
  | DatasetCreatedResult
  | DatasetAllocatedResult
  | DatasetCommittedResult;

export interface HelixDatasetResults {
  [datasetName: string]: HelixDatasetResult;
}

export type HelixDatasetResult =
  | HelixDatasetValidResult
  | HelixDatasetInvalidResult
  | HelixDatasetValidationInProgressResult;

export enum DatasetErrors {
  TOO_MANY_COLUMNS = 'TOO_MANY_COLUMNS',
  COLUMN_TOO_WIDE = 'COLUMN_TOO_WIDE',
  ROW_TOO_WIDE = 'ROW_TOO_WIDE',
  DATA_PROBLEM = 'DATA_PROBLEM',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  EMPTY_DATASET = 'EMPTY_DATASET',
}

export type DatasetError =
  | DatasetErrors.TOO_MANY_COLUMNS
  | DatasetErrors.COLUMN_TOO_WIDE
  | DatasetErrors.ROW_TOO_WIDE
  | DatasetErrors.DATA_PROBLEM
  | DatasetErrors.UNKNOWN_ERROR;

export interface ColumnErrorResult {
  bytes: number;
  canonicalType: string;
  name: string;
  type: string;
  width: number;
}

export interface ColumnTooWideProperties {
  readonly failureCode: DatasetErrors.COLUMN_TOO_WIDE;
  readonly maximumColumnWidth: number;
  readonly columns: ColumnErrorResult[];
}

export interface RowTooWideProperties {
  readonly failureCode: DatasetErrors.ROW_TOO_WIDE;
  readonly maximumRowWidth: number;
  readonly rowWidth: number;
  readonly rowNumber: number;
  readonly columns: ColumnErrorResult[];
}

export interface TooManyColumnsProperties {
  readonly failureCode: DatasetErrors.TOO_MANY_COLUMNS;
  readonly columnCount: number;
  readonly limit: number;
}

export interface DataProblemResult {
  readonly attributeName: string;
  readonly attributeType: string;
  readonly rowNumber: number;
}

export interface DataProblemProperties {
  readonly failureCode: DatasetErrors.DATA_PROBLEM;
  readonly dataProblems: DataProblemResult[];
}

export interface HelixEmptyDatasetProperties {
  readonly code: DatasetErrors.EMPTY_DATASET;
}

export type DatasetStateProperties =
  | ColumnTooWideProperties
  | RowTooWideProperties
  | TooManyColumnsProperties
  | DataProblemProperties;

export type HelixDatasetStateProperties =
  | ColumnTooWideProperties
  | RowTooWideProperties
  | TooManyColumnsProperties
  | DataProblemProperties
  | HelixEmptyDatasetProperties;

export interface DatasetSize {
  readonly rowCount: number;
  readonly columnCount: number;
}

export interface DatasetFailureResult {
  readonly name: string;
  readonly datasetSize: DatasetSize;
  readonly state: DatasetState.Failed;
  readonly stateProperties: DatasetStateProperties;
}

export interface DatasetCreatedResult {
  readonly name: string;
  readonly size: DatasetSize;
  readonly state: DatasetState.Created;
}

export interface DatasetAllocatedResult {
  readonly name: string;
  readonly size: DatasetSize;
  readonly state: DatasetState.Allocated;
}

export interface DatasetCommittedResult {
  readonly name: string;
  readonly size: DatasetSize;
  readonly state: DatasetState.Committed;
}

export interface HelixDatasetValidResult {
  readonly state: DatasetState.Valid;
  readonly stateProperties: HelixDatasetStateProperties;
}

export interface HelixDatasetInvalidResult {
  readonly state: DatasetState.Invalid;
  readonly stateProperties: HelixDatasetStateProperties;
}

export interface HelixDatasetValidationInProgressResult {
  readonly state: DatasetState.ValidationInProgress;
  readonly stateProperties: HelixDatasetStateProperties;
}

export type ReadyResponse = ReadyOkResponse | ReadyErrorResponse;

export type ReadyOkResponse = ReadyCommittedResponse /* | ReadyAllocatedResponse*/;

export type ReadyErrorResponse =
  | DatasetNotFoundResponse
  | DatasetErrorResponse
  | SignatureExpiredResponse
  | ServerErrorResponse
  | TimeoutErrorResponse;

export type HelixReadyResponse =
  | HelixReadyOkResponse
  | HelixDatasetErrorResponse
  | ServerErrorResponse
  | DatasetNotFoundResponse;

export type HelixReadyOkResponse = HelixReadyValidResponse | HelixInProgressResponse;

export interface DatasetErrorResponse {
  readonly result: DatasetFailureResult;
  readonly type: ResponseType.DatasetFailed;
}

export interface ReadyCommittedResponse {
  readonly result: DatasetCommittedResult;
  readonly type: ResponseType.ReadyCommitted;
}

export interface HelixReadyValidResponse {
  readonly result: HelixDatasetValidResult;
  readonly type: ResponseType.ReadyCommitted;
}

export interface HelixInProgressResponse {
  readonly result: HelixDatasetValidationInProgressResult;
  readonly type: ResponseType.HelixInProgress;
}

export interface ServerErrorResponse {
  readonly type: ResponseType.ServerError;
  readonly status: number;
  readonly message: string;
}

export interface SignatureExpiredResponse {
  readonly status: number;
  readonly message: string;
  readonly type: ResponseType.SignatureExpired;
}

export interface DatasetNotFoundResponse {
  readonly status: number;
  readonly message: string;
  readonly type: ResponseType.DatasetNotFound;
}

export interface HelixDatasetErrorResponse {
  readonly result: HelixDatasetInvalidResult;
  readonly type: ResponseType.DatasetFailed;
}

export type SelectResponse = SelectErrorResponse | SelectSuccessResponse;

export type HelixSelectFinalResponse = SelectErrorResponse | HelixSelectSuccessResponse | HelixDatasetErrorResponse;

export type HelixCompleteResponse = HelixEmbeddedResponse | HelixUrlResponse | HelixFlamingoFailedResponse;

export type HelixSelectResponse = HelixCompleteResponse | HelixExecutingResponse;

export interface HelixEmbeddedResponse {
  id: string;
  resultsType: 'EMBEDDED';
  status: HelixResponseStatus.Succeeded;
  results: SelectResult<FlamingoTypes.ModeRole>;
  cacheHit?: boolean;
}

export interface HelixUrlResponse {
  id: string;
  resultsType: 'URL';
  status: HelixResponseStatus.Succeeded;
  results: string;
  cacheHit?: boolean;
}

export interface HelixExecutingResponse {
  id: string;
  status: HelixResponseStatus.Executing;
}

export interface HelixFlamingoFailedResponse {
  id: string;
  status: HelixResponseStatus.Failed;
  statusDetails: FlamingoErrorPayload;
  cacheHit?: boolean;
}

export interface SelectSuccessResponse {
  readonly result: SelectResult<FlamingoTypes.ModeRole>;
  readonly type: ResponseType.SelectSuccess;
}

export interface HelixSelectSuccessResponse {
  readonly result: SelectResult<FlamingoTypes.ModeRole>;
  readonly type: ResponseType.SelectSuccess;
}

export type SelectErrorResponse =
  | DatasetNotFoundResponse
  | DatasetErrorResponse
  | SignatureExpiredResponse
  | ServerErrorResponse
  | UnknownErrorResponse
  | ServiceErrorResponse
  | SizeLimitErrorResponse
  | TimeoutErrorResponse;

export type FlamingoErrorResponse =
  | SelectErrorResponse
  | ReadyErrorResponse
  | HelixDatasetErrorResponse
  | HelixInProgressResponse;

export interface UnknownErrorResponse {
  readonly type: ResponseType.UnknownError;
  readonly status: number;
  readonly message: string;
}

export interface SizeLimitErrorResponse {
  readonly type: ResponseType.SizeLimit;
  readonly limit: number | undefined;
  readonly actual: number;
}

export interface TimeoutErrorResponse {
  readonly type: ResponseType.TimeoutError;
}

/**
 * Response that follow Mode Services error format
 * https://docs.google.com/document/d/1L1KbYLRavaiMHFMyP0G6R0nzTl6DpNvyiNHABYh86c0/edit
 */
export interface ServiceErrorResponse {
  readonly type: ResponseType.ServiceError;
  readonly status: number;
  readonly message: string;
  readonly payload: FlamingoErrorPayload; // We're only concerned about Flamingo errors
}

export interface FlamingoErrorPayload {
  readonly message: string;
  readonly category: FlamingoErrorCategory;
  readonly code: FlamingoErrorCode;
  readonly instanceId: string;
  readonly context: {
    [key: string]: string | any;
  };
  readonly detail: string;
}

export const enum FlamingoErrorCategory {
  InvalidRequest = 'InvalidRequest',
  Unknown = 'Unknown',
  TransientError = 'TransientError',
}

export const enum FlamingoErrorCode {
  DivideByZero = 'DIVIDE_BY_ZERO',
  InvalidRole = 'INVALID_ROLE',
  InvalidSecurityTags = 'INVALID_SECURITY_TAGS',
  DatasetUnathorized = 'DATASET_UNAUTHORIZED',
  GenericError = 'GENERIC_ERROR',
  OutOfCapacity = 'OUT_OF_CAPACITY',
  SelectProcessing = 'SELECT_PROCESSING',
  NoMetadataFound = 'NO_METADATA_FOUND',
  RequestMissingDataset = 'REQUEST_MISSING_DATASET',
  DatasetNotFound = 'DATASET_NOT_FOUND',
  SubresultTooLarge = 'SubresultTooLarge',
  DateOutOfRange = 'DateOutOfRange',
  UnexpectedInfinityOrNaN = 'UnexpectedInfinityOrNaN',
  RowTooWide = 'ROW_TOO_WIDE',
  DataProblem = 'DATA_PROBLEM',
}
export interface FriendlyFlamingoError {
  msg: string;
  helpText?: string;
}
